/**
 * Design tokens from docs/ART_BIBLE.md §2. Single source of truth for CSS custom
 * properties (via `tokensToCss`) and for PixiJS constants (via `hexToNumber`).
 */

export const colors = {
  ink950: '#0A0A18',
  ink900: '#101028',
  night800: '#1A1840',
  night700: '#26225A',
  text: '#EAF1FF',
  textDim: '#A9B4D6',
  neonCyan: '#35E0F0',
  neonPink: '#FF4D9A',
  neonAmber: '#FFB547',
  neonGreen: '#6BFF9E',
  danger: '#FF5A68',
  brass: '#C9A24B',
  brassLight: '#F2D98B',
  brassDark: '#6B531F',
  bpPaper: '#0F3B73',
  bpLine: '#E8F1FF',
  bpChalk: '#F4EBD0',
  newsPaper: '#E9E4D4',
  newsInk: '#1A1A1A',
  lamp: '#FFB866',
  lampText: '#FFE9C7',
  wood: '#3A2418',
} as const;

export type ColorToken = keyof typeof colors;

/**
 * The looks the blueprint (the plan, the maps and the blueprint panels) can have. `classic` is the
 * one in `colors`; the others are cosmetics that playing unlocks. Each keeps the contrast of the
 * classic one (a test checks it).
 */
export const BLUEPRINT_THEMES = {
  classic: { paper: colors.bpPaper, line: colors.bpLine, chalk: colors.bpChalk },
  sepia: { paper: '#3B2A1C', line: '#F5E8CE', chalk: '#E9D8B0' },
  noir: { paper: '#14161D', line: '#E4E7F0', chalk: '#D7D0BC' },
} as const;

export type BlueprintTheme = keyof typeof BLUEPRINT_THEMES;

/** Rules that put a theme on the page: `<html data-blueprint="sepia">`. */
export function blueprintThemesCss(): string {
  return (Object.keys(BLUEPRINT_THEMES) as BlueprintTheme[])
    .filter((name) => name !== 'classic')
    .map((name) => {
      const t = BLUEPRINT_THEMES[name];
      return `html[data-blueprint='${name}']{--c-bp-paper:${t.paper};--c-bp-line:${t.line};--c-bp-chalk:${t.chalk};}`;
    })
    .join('\n');
}

/** Alarm ramp stops (0 %, 50 %, 75 %, 100 %). Interpolated in OKLab by the renderer. */
export const alarmRamp = [
  { at: 0, color: '#3B3A8F' },
  { at: 50, color: '#E8742C' },
  { at: 75, color: '#FF3A3A' },
  { at: 100, color: '#FF2B3A' },
] as const;

export const highContrastColors: Partial<Record<ColorToken, string>> = {
  ink950: '#000000',
  ink900: '#000000',
  night800: '#0B0B0B',
  night700: '#FFFFFF',
  text: '#FFFFFF',
  textDim: '#E6E6E6',
  neonCyan: '#00F0FF',
  neonPink: '#FF5AAE',
  neonAmber: '#FFC857',
  neonGreen: '#7CFFB0',
  danger: '#FF6B78',
};

export const fonts = {
  display: "'Abril Fatface', 'Times New Roman', serif",
  ui: "'Josefin Sans', system-ui, sans-serif",
  typewriter: "'Special Elite', 'Courier New', monospace",
} as const;

/** Type scale in px at UI scale 1 (ART_BIBLE §3). */
export const fontSizes = [12, 14, 16, 20, 24, 32, 48, 72] as const;

export const radii = { sm: 4, md: 8, lg: 14, pill: 999 } as const;

export const spacing = [0, 4, 8, 12, 16, 24, 32, 48, 64] as const;

export const motion = {
  fast: 120,
  base: 220,
  slow: 400,
} as const;

/** Minimum tap target, in CSS px, independent of the UI scale (spec §6). */
export const MIN_TAP_TARGET = 44;

/** `ink900` → `ink-900`, `neonCyan` → `neon-cyan`. */
const kebab = (s: string): string =>
  s
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([a-z])(\d)/g, '$1-$2')
    .toLowerCase();

export function hexToNumber(hex: string): number {
  return Number.parseInt(hex.replace('#', ''), 16);
}

function colorDeclarations(map: Partial<Record<ColorToken, string>>): string {
  return Object.entries(map)
    .map(([k, v]) => `--c-${kebab(k)}:${v};`)
    .join('');
}

/** Builds the `:root` stylesheet with every token as a CSS custom property. */
export function tokensToCss(): string {
  const base = [
    colorDeclarations(colors),
    `--f-display:${fonts.display};--f-ui:${fonts.ui};--f-typewriter:${fonts.typewriter};`,
    ...Object.entries(radii).map(([k, v]) => `--r-${k}:${v}px;`),
    ...Object.entries(motion).map(([k, v]) => `--t-${k}:${v}ms;`),
    `--min-tap:${MIN_TAP_TARGET}px;`,
  ].join('');
  const hc = colorDeclarations(highContrastColors);
  return `:root{${base}}:root[data-contrast="high"]{${hc}}`;
}
